import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/solid';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  isCurrentPage?: boolean;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
  showHome?: boolean;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ 
  items = [], 
  className = '',
  showHome = true 
}) => {
  const location = useLocation();

  // Generate breadcrumbs from URL if no items provided
  const generateBreadcrumbsFromPath = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];
    
    let currentPath = '';
    
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Convert segment to readable label
      let label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Handle special cases
      if (segment === 'listing') label = 'Card Details';
      if (segment === 'storefront') label = 'Store';
      if (segment === 'search') label = 'Search Results';
      if (segment === 'seller') label = 'Seller';
      if (segment === 'admin') label = 'Admin';
      if (segment === 'dashboard') label = 'Dashboard';
      if (segment === 'profile') label = 'Profile';
      if (segment === 'orders') label = 'Orders';
      if (segment === 'offers') label = 'Offers';
      if (segment === 'cart') label = 'Shopping Cart';
      if (segment === 'become-seller') label = 'Become a Seller';
      if (segment === 'subscription-tiers') label = 'Pricing';
      if (segment === 'listings') label = 'Browse Cards';
      if (segment === 'inbox') label = 'Messages';
      if (segment === 'shipping') label = 'Shipping';
      
      breadcrumbs.push({
        label,
        path: index === pathSegments.length - 1 ? undefined : currentPath,
        isCurrentPage: index === pathSegments.length - 1
      });
    });
    
    return breadcrumbs;
  };

  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbsFromPath();

  // Add home breadcrumb if requested and not already present
  const finalBreadcrumbs = showHome && breadcrumbItems[0]?.label !== 'Home' 
    ? [{ label: 'Home', path: '/' }, ...breadcrumbItems]
    : breadcrumbItems;

  if (finalBreadcrumbs.length <= 1) {
    return null; // Don't show breadcrumbs if we're just on the home page
  }

  return (
    <nav 
      className={`flex items-center space-x-1 text-sm text-gray-500 ${className}`}
      aria-label="Breadcrumb"
    >
      {finalBreadcrumbs.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <ChevronRightIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          )}
          
          {item.isCurrentPage || !item.path ? (
            <span 
              className={`${
                item.isCurrentPage 
                  ? 'text-gray-900 font-medium' 
                  : 'text-gray-500'
              } truncate`}
              aria-current={item.isCurrentPage ? 'page' : undefined}
            >
              {index === 0 && showHome ? (
                <HomeIcon className="h-4 w-4 inline" aria-hidden="true" />
              ) : (
                item.label
              )}
            </span>
          ) : (
            <Link
              to={item.path}
              className="text-blue-600 hover:text-blue-800 hover:underline transition-colors truncate"
            >
              {index === 0 && showHome ? (
                <HomeIcon className="h-4 w-4 inline" aria-hidden="true" />
              ) : (
                item.label
              )}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumbs; 